import React, { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useLanguage } from "@/i18n/LanguageProvider";
import { edgeLocaleFor } from "@/i18n/locale";
import { Sparkles, Clock, Dumbbell, Layers, Weight, Star, Loader2 } from "lucide-react";
import { useWorkoutReact } from "@/hooks/useWorkoutReact";
import ReportContentButton from "@/components/report/ReportContentButton";
import { REPORT_SURFACES } from "@/lib/report/surfaces";
import { useReportedContent } from "@/contexts/ReportedContentContext";

interface CompletionParticle {
  id: number;
  left: string;
  top: string;
  size: number;
  color: string;
  dur: number;
  delay: number;
}

const CELEBRATION_PARTICLES: CompletionParticle[] = Array.from({ length: 8 }, (_, i) => ({
  id: i,
  left: `${10 + Math.random() * 80}%`,
  top: `${10 + Math.random() * 80}%`,
  size: 1.5 + Math.random() * 2,
  color: i % 3 === 0 ? "hsl(140 60% 55%)" : i % 3 === 1 ? "#00e5ff" : "hsl(265 60% 65%)",
  dur: 6 + Math.random() * 4,
  delay: Math.random() * 2,
}));

interface WorkoutCompletionProps {
  totalTime: number;
  exercisesDone: number;
  totalExercises: number;
  setsDone: number;
  totalSets: number;
  totalVolume: number;
  xpAwarded: number;
  workoutSessionId?: string | null;
  onFinish: () => void;
}

const WorkoutCompletion: React.FC<WorkoutCompletionProps> = ({
  totalTime, exercisesDone, totalExercises, setsDone, totalSets, totalVolume, xpAwarded, workoutSessionId, onFinish,
}) => {
  const { t, lang } = useLanguage();
  const reducedMotion = useReducedMotion();
  const mirrorReact = useWorkoutReact();
  const { isReported } = useReportedContent();
  const [showXP, setShowXP] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowXP(true), 800);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!workoutSessionId) return;
    const langCode = edgeLocaleFor(lang);
    mirrorReact.react(workoutSessionId, langCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workoutSessionId, lang]);

  return (
    <motion.div
      className="fixed inset-0 z-50 flex flex-col items-center px-6 py-10 overflow-y-auto"
      style={{ background: "rgba(5,8,16,0.96)", paddingBottom: "max(2.5rem, env(safe-area-inset-bottom))" }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      {/* Celebration particles */}
      {!reducedMotion && CELEBRATION_PARTICLES.map(p => (
        <motion.div
          key={p.id}
          className="absolute rounded-full pointer-events-none"
          style={{ width: p.size, height: p.size, background: p.color, left: p.left, top: p.top }}
          animate={{ y: [0, -(10 + Math.random() * 15), 0], opacity: [0.15, 0.3, 0.15] }}
          transition={{ duration: p.dur, repeat: Infinity, ease: "easeInOut", delay: p.delay }}
        />
      ))}

      {/* Glow */}
      <motion.div
        className="absolute w-[350px] h-[350px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(0,229,255,0.06) 0%, rgba(140,200,100,0.03) 50%, transparent 70%)" }}
        animate={reducedMotion ? {} : { scale: [0.9, 1.1, 0.9], opacity: [0.4, 0.7, 0.4] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Icon */}
      <motion.div
        className="w-20 h-20 shrink-0 rounded-full flex items-center justify-center mb-5 relative z-10"
        style={{
          background: "hsl(140 50% 40% / 0.15)",
          border: "2px solid hsl(140 50% 45%)",
          boxShadow: "0 0 40px hsl(140 50% 45% / 0.3), 0 0 80px hsl(140 50% 45% / 0.1)",
        }}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
      >
        <Sparkles size={32} style={{ color: "hsl(140 60% 60%)" }} />
      </motion.div>

      {/* Title */}
      <motion.p
        className="font-display text-[24px] font-bold text-white mb-1 relative z-10"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        {t("fitness.workout.complete")}
      </motion.p>

      {/* XP badge */}
      {showXP && (
        <motion.div
          className="flex items-center gap-2 mb-6 px-4 py-2 rounded-full relative z-10"
          style={{ background: "hsl(45 90% 55% / 0.12)", border: "1px solid hsl(45 90% 55% / 0.30)" }}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <span className="text-[14px]">+{xpAwarded}</span>
          <span className="font-display text-[13px] font-bold" style={{ color: "hsl(45 90% 60%)" }}>XP</span>
        </motion.div>
      )}

      {/* Stats grid */}
      <motion.div
        className="grid grid-cols-2 gap-3 w-full max-w-xs mb-8 relative z-10"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        {[
          { icon: Clock, label: t("fitness.workout.totalTime"), value: `${totalTime} ${t("fitness.min")}` },
          { icon: Dumbbell, label: t("fitness.workout.exercisesDone"), value: `${exercisesDone}/${totalExercises}` },
          { icon: Layers, label: t("fitness.workout.totalSeries"), value: `${setsDone}/${totalSets}` },
          { icon: Weight, label: t("fitness.workout.totalVolume"), value: `${totalVolume.toLocaleString()} kg` },
        ].map((s, i) => (
          <motion.div
            key={s.label}
            className="rounded-xl p-3 text-center"
            style={{ background: "hsl(187 72% 50% / 0.06)", border: "1px solid hsl(187 72% 50% / 0.15)" }}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 + i * 0.1 }}
          >
            <s.icon size={16} className="mx-auto mb-1.5" style={{ color: "hsl(187 72% 55%)" }} />
            <p className="font-display text-[18px] font-bold text-foreground">{s.value}</p>
            <p className="text-[9px] font-body mt-0.5" style={{ color: "hsl(218 14% 50%)" }}>{s.label}</p>
          </motion.div>
        ))}
      </motion.div>

      {/* Mirror reaction (real, Padrão B) */}
      {(mirrorReact.isLoading || mirrorReact.reaction) &&
       !isReported(REPORT_SURFACES.REACTION_WORKOUT, workoutSessionId) && (
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-xs rounded-2xl p-5 relative overflow-hidden mb-6 z-10"
          style={{
            background: "linear-gradient(135deg, rgba(0,0,0,0.30) 0%, rgba(10,8,20,0.45) 100%)",
            border: "1.5px solid hsl(187 72% 50% / 0.55)",
            boxShadow: "0 0 30px rgba(0,229,255,0.06), inset 0 1px 0 hsl(187 72% 50% / 0.15)",
          }}
        >
          <div className="flex items-start gap-3 relative z-10">
            <motion.div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5"
              style={{ background: "rgba(0,229,255,0.08)", border: "1px solid rgba(0,229,255,0.18)" }}
              animate={{ boxShadow: ["0 0 0px rgba(0,229,255,0)", "0 0 16px rgba(0,229,255,0.2)", "0 0 0px rgba(0,229,255,0)"] }}
              transition={{ duration: 4, repeat: Infinity }}
            >
              <Star size={14} style={{ color: "#00e5ff" }} />
            </motion.div>
            <div className="flex-1 min-w-0">
              <p className="text-[9px] font-display tracking-[0.2em] uppercase mb-2" style={{ color: "rgba(0,229,255,0.80)" }}>
                MIRROR
              </p>
              {mirrorReact.isLoading ? (
                <div className="flex items-center gap-2 py-2">
                  <Loader2 size={12} className="animate-spin" style={{ color: "rgba(0,229,255,0.5)" }} />
                  <p className="text-[11px] font-body italic" style={{ color: "rgba(255,255,255,0.5)" }}>{t("mirror.thinking")}</p>
                </div>
              ) : (
                <>
                  <p className="text-[12px] font-body leading-relaxed italic" style={{ color: "rgba(255,255,255,0.88)" }}>
                    {mirrorReact.reaction}
                  </p>
                  {/* Denuncia da reacao ao treino. Identidade = a sessao de
                      treino, unica por reacao (o cache do workout-react e
                      chaveado nela). */}
                  {workoutSessionId && mirrorReact.reaction && (
                    <div className="flex justify-end -mr-1 mt-1">
                      <ReportContentButton
                        surface={REPORT_SURFACES.REACTION_WORKOUT}
                        contentId={workoutSessionId}
                        snapshot={mirrorReact.reaction}
                        context={{ module: "fitness" }}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </motion.div>
      )}

      {/* Finish button */}
      <motion.button
        onClick={onFinish}
        className="w-full max-w-xs py-4 rounded-xl font-display text-[15px] font-bold tracking-wide active:scale-[0.97] transition-transform relative z-10"
        style={{
          background: "linear-gradient(135deg, hsl(187 72% 48%), hsl(187 72% 42%))",
          color: "#0a0a0f",
          boxShadow: "0 0 20px hsl(187 72% 50% / 0.2)",
        }}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
      >
        {t("fitness.workout.finishBtn")}
      </motion.button>
    </motion.div>
  );
};

export default React.memo(WorkoutCompletion);
